// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useState, useMemo } from 'react';
import { Tab, Tabs } from '@mui/material';
import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { packageId as pIDCred } from '@daml.js/dtc-credential/lib';
import { packageId as pIDRegApp } from '@daml.js/dtc-registry-app/lib';
import { packageId as pIDRegHolding } from '@daml.js/dtc-registry-holding/lib';
import { packageId as pIDReg } from '@daml.js/dtc-registry/lib';
import { packageId as pIDSpliceAmulet } from '@daml.js/splice-amulet/lib';
import { packageId as pIDSpliceApiTokenAllocInst } from '@daml.js/splice-api-token-allocation-instruction/lib';
import { packageId as pIDSpliceApiTokenAllocReq } from '@daml.js/splice-api-token-allocation-request/lib';
import { packageId as pIDSpliceApiTokenAlloc } from '@daml.js/splice-api-token-allocation/lib';
import { packageId as pIDSpliceApiTokenHolding } from '@daml.js/splice-api-token-holding/lib';
import { packageId as pIDSpliceApiTokenMetadata } from '@daml.js/splice-api-token-metadata/lib';
import { packageId as pIDSpliceApiTokenTransferInst } from '@daml.js/splice-api-token-transfer-instruction/lib';
import { DataTable } from '../../../components/Table/DataTable';
import { useLedgerPackageList } from '../../../hooks/queries/ledger/useLedgerPackageList';
import { PageWrapper } from '../../PageWrapper';

type ExpectedPackage = {
  packageName: string;
  packageId: string;
};

const expectedPackages: ExpectedPackage[] = [
  { packageName: 'dtc-credential', packageId: pIDCred },
  { packageName: 'dtc-registry-app', packageId: pIDRegApp },
  { packageName: 'dtc-registry-holding', packageId: pIDRegHolding },
  { packageName: 'dtc-registry', packageId: pIDReg },
  { packageName: 'splice-amulet', packageId: pIDSpliceAmulet },
  { packageName: 'splice-api-token-allocation', packageId: pIDSpliceApiTokenAlloc },
  { packageName: 'splice-api-token-allocation-instruction', packageId: pIDSpliceApiTokenAllocInst },
  { packageName: 'splice-api-token-allocation-request', packageId: pIDSpliceApiTokenAllocReq },
  { packageName: 'splice-api-token-holding', packageId: pIDSpliceApiTokenHolding },
  { packageName: 'splice-api-token-metadata', packageId: pIDSpliceApiTokenMetadata },
  {
    packageName: 'splice-api-token-transfer-instruction',
    packageId: pIDSpliceApiTokenTransferInst,
  },
];

const FrontEndPackageBindings = () => {
  const ledgerPackageList = useLedgerPackageList();

  const ledgerPackageSet = useMemo(() => {
    return new Set(ledgerPackageList?.packageIds || []);
  }, [ledgerPackageList]);

  const headers: GridColDef<ExpectedPackage>[] = useMemo(() => {
    return [
      {
        field: 'packageName',
        headerName: 'Package Name',
        flex: 2,
        filterable: true,
      },
      {
        field: 'packageId',
        headerName: 'Package Id',
        flex: 4,
        filterable: true,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        filterable: true,
        align: 'center',
        renderCell: (params) => (ledgerPackageSet.has(params.row.packageId) ? 'Ok' : 'MISSING'),
      },
    ];
  }, [ledgerPackageSet]);

  return (
    <DataTable
      columns={headers}
      rows={expectedPackages}
      rowKey={(d) => `${d.packageName}-${d.packageId}`}
    />
  );
};

const LedgerPackageList = () => {
  const ledgerPackageList = useLedgerPackageList();

  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'scroll' }}>
      <ul>
        {(ledgerPackageList?.packageIds || []).map((pid) => (
          <li key={pid}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '8pt' }}>{pid}</Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
};

export const LedgerStatus = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const onChangeTab = (_: React.SyntheticEvent, newCurrentTab: number) => {
    setCurrentTab(newCurrentTab);
  };

  const tabs: { [key: number]: React.ReactNode } = {
    0: <FrontEndPackageBindings />,
    1: <LedgerPackageList />,
  };

  return (
    <PageWrapper
      buttons={[]}
      pageName={
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1 }}>
          <Typography
            sx={{ textTransform: 'capitalize' }}
            fontWeight="bold"
            color="primary"
            variant="h4"
          >
            Ledger Status
          </Typography>
        </Box>
      }
    >
      <Tabs variant="scrollable" value={currentTab} onChange={onChangeTab}>
        <Tab value={0} label="Front End Package Bindings" />
        <Tab value={1} label="Ledger Package List" />
      </Tabs>

      {tabs[currentTab]}
    </PageWrapper>
  );
};
